import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Person as PersonIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { loginUser } from '../store/authActions';
import { selectAuthLoading, selectAuthError, clearError } from '../store/authSlice';
import { DEMO_CREDENTIALS } from '../config/routes';

const LoginPage = ({ onLogin }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    keepLoggedIn: false
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (field) => (event) => {
    const value = field === 'keepLoggedIn' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) dispatch(clearError());
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const result = await dispatch(loginUser({
      username: formData.username,
      password: formData.password,
      keepLoggedIn: formData.keepLoggedIn
    }));

    if (result.success) {
      // Store keep logged in preference
      if (formData.keepLoggedIn) {
        localStorage.setItem('keepLoggedIn', 'true');
        localStorage.setItem('username', formData.username);
      }
      
      // Call onLogin callback
      onLogin({
        username: formData.username,
        success: true
      });
    }
  };

  const handleForgotPassword = () => {
    alert(`For demo purposes, use:\nUsername: ${DEMO_CREDENTIALS.username}\nPassword: ${DEMO_CREDENTIALS.password}`);
  };

  return (
    <Card sx={{ 
      width: '100%',
      height: '100vh',
      borderRadius: 0,
      boxShadow: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'white'
    }}>
        <CardContent sx={{ 
          p: { xs: 3, sm: 4 },
          width: '100%',
          maxWidth: '350px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              AI-Powered EV Charging
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Management System
            </Typography>
            <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
              Welcome back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please enter your login details.
            </Typography>
          </Box>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Modern Login Form */}
          <Stack component="form" onSubmit={handleSubmit} spacing={2.5}>
            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              value={formData.username}
              onChange={handleInputChange('username')}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={formData.password}
              onChange={handleInputChange('password')}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'primary.main' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.keepLoggedIn}
                    onChange={handleInputChange('keepLoggedIn')}
                    color="primary"
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Keep me logged in
                  </Typography>
                }
              />
              <Link 
                component="button"
                variant="body2"
                type="button"
                onClick={handleForgotPassword}
                sx={{ 
                  textDecoration: 'none',
                  color: 'primary.main',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={!loading && <LoginIcon />}
              sx={{
                py: 1.8,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <Typography variant="button" color="white">
                    Signing in...
                  </Typography>
                </Box>
              ) : (
                'Sign In to Dashboard'
              )}
            </Button>
          </Stack>

          {/* Modern Footer */}
          <Box sx={{ 
            textAlign: 'center', 
            mt: 4,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                fontWeight: 500
              }}
            >
              © 2025 Zynka Operator
              <Box component="span" sx={{ color: 'primary.main' }}>•</Box>
              All rights reserved
            </Typography>
          </Box>
        </CardContent>
    </Card>
  );
};

export default LoginPage;